import * as THREE from "three";
import InputCommand from "./InputCommand";
import CommandIdEnum from "../enums/CommandIdEnums";
import CommandStack from "../logic/CommandStack";
import ICanvasInputListener from "../logic/ICanvasInputListener";
import CanvasInputListenerModeEnum from "../enums/CanvasInputListenerModeEnum";
import RaycastIntersection from "../logic/RaycastIntersection";
import { SceneManager } from "../model/SceneManager";
import BundleHighlighter, { getProductGlobalId } from "../logic/BundleHighlighter";
import IParameterizable from "../interfaces/IParameterizable";
import ICoreBundle from "../interfaces/ICoreBundle";
import { BundleElement, useElementBundlesStore } from "../store/elementBundlesStore";
import { useCurrentCommandState } from "../store/currentCommandStateStore";
import { useDetachDialogStore } from "../store/detachDialogStore";
import { useServicesState } from "../store/servicesStateStore";
import { normalizeGuid } from "../utils/guidUtils";
import { DetachRoomElementsBody } from "../services/coreApi/BundleDetachResult";
import { ICoreApartmentRoom } from "../interfaces/ICoreApartmentBundles";

export interface DetachBundleElementsParameters {
    bundle: ICoreBundle;
    elements: BundleElement[];      // The bundle's elements, the ones that can be picked
    rooms: ICoreApartmentRoom[];    // The apartment's rooms, for placing the new bundle
    onDetached: (newBundleId: number) => void;  // Called after a detach, once the bundles have been reloaded
}

// The one room every element is listed in, and no other; null when they span several rooms or none
export const getCommonRoomId = (elements: BundleElement[]): number | null => {
    const roomIds = new Set(elements.flatMap(e => e.roomIds));
    return roomIds.size === 1 ? [...roomIds][0] : null;
};

/**
 * Picks elements of one bundle in the model and detaches them into a new bundle through CoreAPI.
 * Started from the bundle panel on top of the Bundle Inspector, which resumes when this ends.
 * The bundle's elements are highlighted and the rest ghosted; clicking one toggles it in the selection.
 * Detach opens the dialog that sets up the new bundle and sends the request.
 */
class DetachBundleElementsCmd extends InputCommand implements IParameterizable<DetachBundleElementsParameters> {

    #scene: THREE.Object3D;
    #highlighter: BundleHighlighter;
    #params: DetachBundleElementsParameters | null = null;
    #candidates = new Map<string, BundleElement>();     // By normalised globalId
    #selected = new Set<string>();
    #ended = false;

    constructor(commandStack: CommandStack,
                canvasInputListener: ICanvasInputListener,
                scene: THREE.Object3D = SceneManager.scene) {
        super(CommandIdEnum.DetachBundleElements, commandStack, canvasInputListener);
        this.commandNameResourceKey = 'command.detachBundleElements.title';
        this.#scene = scene;
        this.#highlighter = new BundleHighlighter(scene);
    }

    setParameters(params: DetachBundleElementsParameters): void {
        this.#params = params;
        this.#candidates = new Map(params.elements.map(e => [e.globalId, e]));
    }

    start() {
        super.start();
        this.addToStack();
        this.connectMouse();
        this.canvasInputListener.setRaycastMode(CanvasInputListenerModeEnum.RAYCAST_TARGET_OBJECTS);
        this.canvasInputListener.resetTargetObjects();
        // Only the bundle's elements, so ghosted ones in front never get in the way
        this.canvasInputListener.setHitFilter(object => this.#candidateOf(object) !== null);
        useCurrentCommandState.getState().showNextButton(() => this.openDialog(), 'command.detachBundleElements.detach', false);
        this.update();
    }

    #candidateOf(object: THREE.Object3D | null): string | null {
        const globalId = normalizeGuid(getProductGlobalId(object));
        return globalId && this.#candidates.has(globalId) ? globalId : null;
    }

    getSelectedElements(): BundleElement[] {
        return [...this.#candidates.values()].filter(e => this.#selected.has(e.globalId));
    }

    // A bundle must keep at least one element
    canDetach(): boolean {
        return this.#selected.size > 0 && this.#selected.size < this.#candidates.size;
    }

    update() {
        this.#highlighter.apply(this.#selected, new Set(this.#candidates.keys()));
        this.showInfoMessage('command.detachBundleElements.prompt', { count: this.#selected.size });
        useCurrentCommandState.getState().setNextButtonEnabled(this.canDetach());
    }

    getCursorName(): string {
        return 'pointer';
    }

    pointerMove(): boolean {
        return true;
    }

    // Toggles the clicked element. Clicks are always consumed, and empty space does nothing.
    point(raycastResult: RaycastIntersection | undefined): boolean {
        const globalId = this.#candidateOf(raycastResult?.object ?? null);
        if (!globalId) return true;

        if (this.#selected.has(globalId)) this.#selected.delete(globalId);
        else this.#selected.add(globalId);
        this.update();
        return true;
    }

    rightClick(): void {
        // No input plane to switch
    }

    // Works in both 2D and 3D
    onViewerModeChange(): void {
        this.update();
    }

    openDialog() {
        if (!this.#params || !this.canDetach()) return;

        const { bundle, rooms } = this.#params;
        const elements = this.getSelectedElements();
        useDetachDialogStore.getState().open({
            bundleName: bundle.name,
            elements,
            rooms,
            defaultLevel: bundle.roomId !== null ? 'room' : 'apartment',
            // The room the picked elements are all in, else the original bundle's
            defaultRoomId: getCommonRoomId(elements) ?? bundle.roomId,
            onConfirm: (body) => this.detach(body),
            onCancel: () => useDetachDialogStore.getState().close(),   // Back to picking, the selection kept
        });
    }

    async detach(body: DetachRoomElementsBody) {
        const dialog = useDetachDialogStore.getState();
        const dataProvider = useServicesState.getState().dataProvider;
        if (!dataProvider || dialog.busy) return;

        dialog.setBusy(true);
        const result = await dataProvider.detachBundleElements(body);
        if (this.#ended) return;

        if (!result.ok) {
            dialog.setBusy(false);
            dialog.setError(result.error);
            return;
        }

        // Detached; reloading only refreshes what's shown, so a failure there is no error
        await useElementBundlesStore.getState().reload(() => dataProvider.loadElementBundles());
        if (this.#ended) return;

        this.#params?.onDetached(result.newBundleId);
        this.end();
    }

    end() {
        if (this.#ended) return;
        this.#ended = true;
        useDetachDialogStore.getState().close();
        useCurrentCommandState.getState().hideNextButton();
        this.#highlighter.dispose();
        this.canvasInputListener.setHitFilter(null);
        this.canvasInputListener.resetTargetObjects();
        super.end();
        this.removeFromStack();
    }
}

export default DetachBundleElementsCmd;
